import React from 'react'
import moment from 'moment'
import { Dropdown, Form, Grid } from 'semantic-ui-react'
import DateTimePicker from '@cbgms/components/form/DateTimePickerBasic'
import { formatters } from '@cbgms/base/utils/datetime'
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { useNotifier } from '@cbgms/base/notifier'
import { useDispatch, useSelector } from 'react-redux'
import {
  getShopCountries,
  setCompanyUUID,
  setEndDate,
  setFilteredByModule,
  setShopID,
  setStartDate,
  setStatsCountry
} from '../../../state/requests'
import { IRootReducerState } from 'state/reducers'
import { listCompanies } from 'app-support/company/state/actions'
import { listShops } from 'app-support/shops/state/actions'
import { useQuery } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import { Redirect } from 'react-router-dom'
import { EModules } from 'app-support/stats/state/reducer'
import { CountryCode } from '@carsys/enums/country-code'
import { Locales } from '@carsys/enums/locales'

const listOfModules = [
  { text: 'GMS & Portal', value: '' },
  { text: 'GMS', value: EModules.GMS },
  { text: 'Portal', value: EModules.PORTAL }
]

const dateFormat = 'DD-MM-YYYY'

const overflowStyling = {
  maxWidth: 165
}

const StatsDashboardDropdowns: React.FC<{ showDateFilters?: boolean }> = ({ showDateFilters = true }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()

  const { country, startDate, endDate, companyUUID, shopID, filteredByModule } = useSelector((state: IRootReducerState) => state.stats)
  const countriesResult = useQuery(
    ['Statistics', 'Countries', i18n.language],
    async () => {
      const countries = await dispatch(getShopCountries(i18n.language as Locales))
      return countries
    },
    { refetchOnWindowFocus: false }
  )
  const companyAndShopResult = useQuery(
    ['Statistics', 'Filters', filteredByModule, country],
    async () => {
      let shopSearch = ''
      switch (filteredByModule) {
        case EModules.GMS:
          shopSearch = 'Shop.ModuleGMSEnabled|=|true,Shop.ModulePortalEnabled|=|false'
          break
        case EModules.PORTAL:
          shopSearch = 'Shop.ModulePortalEnabled|=|true,Shop.ModuleGMSEnabled|=|false'
          break
      }
      if (country) {
        if (shopSearch) {
          shopSearch += ','
        }
        shopSearch += `Shop.VisitorCountry|=|${country}`
      }

      const [companiesResult, shopsResult] = await Promise.all([
        dispatch(listCompanies({ perPage: 25000, fields: 'Company.UUID,Company.Name' })),
        dispatch(listShops({ perPage: 25000, fields: 'Shop.UUID,Shop.Name', search: shopSearch }))
      ])

      return {
        companies: companiesResult.Data,
        shops: shopsResult.Data
      }
    },
    { refetchOnWindowFocus: false }
  )

  if (companyAndShopResult.isFetched && isEmpty(companyAndShopResult.data)) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/stats' />
  }

  const listOfShops =
    companyAndShopResult.data?.shops.map(shop => ({
      text: (
        <div className='d-flex' title={`${shop.Shop.Name} (${shop.Shop.ID})`}>
          <div className='overflow-ellipsis' style={overflowStyling}>
            {shop.Shop.Name}
          </div>
          &nbsp;
          <div>({shop.Shop.ID})</div>
        </div>
      ),
      value: shop.Shop.ID,
      companyuuid: shop.Shop.CompanyUUID
    })) || []

  const listOfCompanies =
    companyAndShopResult.data?.companies.map(company => ({
      text: company.Company.Name,
      value: company.Company.UUID
    })) || []

  const listOfCountries =
    countriesResult.data?.Data.map(c => {
      return { text: c.Name, value: c.IsoCode }
    }) || []

  const filteredShops = listOfShops.filter(shop => shop.companyuuid === companyUUID) || []

  return (
    <Grid.Row columns={16} as={Form} className='align-items-end'>
      {showDateFilters && (
        <>
          <Grid.Column width={3}>
            <DateTimePicker
              className='date_picker'
              viewMode='days'
              formatters={formatters.date}
              timeFormat={false}
              meta={{} as WrappedFieldMetaProps}
              label={t('global.stats.startdate')}
              required
              input={
                {
                  value: startDate,
                  onChange: (e: React.ChangeEvent<HTMLInputElement> | moment.Moment) => {
                    if (moment.isMoment(e)) {
                      // when making a selection in the datetime popup we receive a moment.Moment
                      dispatch(setStartDate(formatters.date.toUserFormat(e)))
                    } else {
                      // when typing we receive a React.ChangeEvent
                      const value = e.target.value
                      dispatch(setStartDate(value))
                    }
                  },
                  onBlur: (_: string) => {
                    const momentStartDate = formatters.date.fromUserFormat(startDate)

                    const momentEndDate = moment(endDate, dateFormat)
                    if (momentEndDate.isAfter(momentStartDate) || momentEndDate.isSame(momentStartDate)) {
                      dispatch(setStartDate(moment(momentStartDate).format(dateFormat)))
                    } else {
                      notifier.error(t('global.stats.startdate_error'))
                    }
                  }
                } as WrappedFieldInputProps
              }
            />
          </Grid.Column>
          <Grid.Column width={3}>
            <DateTimePicker
              className='date_picker'
              viewMode='days'
              formatters={formatters.date}
              timeFormat={false}
              meta={{} as WrappedFieldMetaProps}
              label={t('global.stats.enddate')}
              required
              input={
                {
                  value: endDate,
                  onChange: (e: React.ChangeEvent<HTMLInputElement> | moment.Moment) => {
                    if (moment.isMoment(e)) {
                      // when making a selection in the datetime popup we receive a moment.Moment
                      dispatch(setEndDate(formatters.date.toUserFormat(e)))
                    } else {
                      // when typing we receive a React.ChangeEvent
                      const value = e.target.value
                      dispatch(setEndDate(value))
                    }
                  },
                  onBlur: (_: string) => {
                    const momentEndDate = formatters.date.fromUserFormat(endDate)
                    const momentStartDate = moment(startDate, dateFormat)
                    if (momentStartDate.isBefore(momentEndDate) || momentStartDate.isSame(momentEndDate)) {
                      dispatch(setEndDate(moment(momentEndDate).format(dateFormat)))
                    } else {
                      notifier.error(t('global.stats.enddate_error'))
                    }
                  }
                } as WrappedFieldInputProps
              }
            />
          </Grid.Column>
        </>
      )}
      <Grid.Column floated='right' width={2}>
        <Dropdown
          placeholder={t('global.all')}
          fluid
          selection
          clearable
          selectOnBlur={false}
          options={listOfCountries}
          value={country}
          onChange={(_e, { value }) => dispatch(setStatsCountry(value as CountryCode | ''))}
        />
      </Grid.Column>
      <Grid.Column width={2}>
        <Dropdown
          placeholder={t('stats.allApplications')}
          fluid
          selection
          selectOnBlur={false}
          options={listOfModules}
          value={filteredByModule}
          onChange={(_e, { value }) => dispatch(setFilteredByModule(value as string))}
        />
      </Grid.Column>
      <Grid.Column width={3}>
        <Dropdown
          placeholder={t('global.menu_companies')}
          fluid
          search
          selection
          clearable
          disabled={!!companyUUID && !!shopID}
          selectOnBlur={false}
          options={listOfCompanies}
          value={companyUUID}
          onChange={(_e, data) => {
            dispatch(setCompanyUUID(data.value as string))
          }}
        />
      </Grid.Column>
      <Grid.Column width={3}>
        <Dropdown
          className='shops-dropdown'
          placeholder={t('global.menu_shops')}
          fluid
          search
          selection
          clearable
          disabled={!companyUUID}
          options={filteredShops}
          value={shopID}
          onChange={(_e, data) => {
            dispatch(setShopID(data.value as string))
          }}
        />
      </Grid.Column>
    </Grid.Row>
  )
}

export default StatsDashboardDropdowns
